import {
  cloneElement, FC, isValidElement, ReactNode,
} from 'react';
import { useTranslation } from 'next-i18next';
import { ADMIN_PREFIX } from 'src/common/constants';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import Icon from 'src/common/components/IconDS22';
import getClassNames from '../../utils/getClassNames';
import NavDropdown from '../NavDropdown';
import SubNavigation from './SubNavigation';
import UnauthenticatedNavItems from './UnauthenticatedNavItems';
import style from './Navigation.module.scss';
import { useUserContext } from '../../providers/UserProvider';
import DestinationNavigation from '../DestinationNavigation';
import useNavigationConfig from './config';
import NavItem from './NavItem';
import CurrencySelectorInput from '../CurrencySelectorInput';
import NavModal from '../NavModal';
import TopNavItemWrapper from './TopNavItemWrapper';
import type { MainNavItemComponentType, MainNavItemLinkType, MainNavItemType } from './types';

type NavigationProps = {
};

type ScreenSizeWrapperProps = {
  screenSizeWrapper: 'm' | 'mDown';
  children: ReactNode;
};

const ScreenSizeWrapper: FC<ScreenSizeWrapperProps> = ({ screenSizeWrapper, children }) => (
  <div className={getClassNames(style, 'screenSizeWrapper', classNames({ [screenSizeWrapper]: true }))}>
    {children}
  </div>
);

const Navigation: FC<NavigationProps> = () => {
  const { user, userRole } = useUserContext();
  const [t] = useTranslation(['navbar', 'common']);
  const isLoggedIn = !!user?.id; // User is authenticated if it has ID
  const isOwner = userRole === 'owner';
  const { pathname } = useRouter();
  const isAdminPath = pathname.startsWith(ADMIN_PREFIX);
  const modifiers = `mainNav ${isLoggedIn ? 'authenticated' : 'anonymous'} ${isAdminPath ? 'admin' : ''}`;
  const configItems = useNavigationConfig() as MainNavItemType[];

  let menuItems = userRole === 'captain'
    ? configItems.filter(
      (config) => config.isHeaderNavItem?.owner && (config.roles == null || config.roles.includes('captain')),
    )
    : configItems.filter(
      (config) => config.isHeaderNavItem?.renter && (config.roles == null || config.roles.includes('renter')),
    );

  if (isOwner) {
    menuItems = configItems.filter(
      (config) => config.isHeaderNavItem?.owner && (config.roles == null || config.roles.includes('owner')),
    );
  }

  return (
    <nav className={style.root}>
      <ul className={style.menu}>
        {!isOwner && isLoggedIn && (
          <CurrencySelectorInput
            renderAs="li"
            location="topNav"
          />
        )}
        {isLoggedIn && menuItems.map((navItemConfig: (
          MainNavItemComponentType | MainNavItemLinkType
        )) => {
          const screenSizeModifiers = isAdminPath
            ? navItemConfig.isHeaderNavItem?.ownerAdmin?.join(' ')
            : navItemConfig.isHeaderNavItem?.[isOwner ? 'owner' : 'renter']?.join(' ');
          const iconOnlyScreenSizeModifiers = isAdminPath
            ? navItemConfig.iconOnlyScreenSizes?.ownerAdmin?.map((size) => `iconOnly_${size}`).join(' ')
            : navItemConfig.iconOnlyScreenSizes?.[isOwner ? 'owner' : 'renter']?.map((size) => `iconOnly_${size}`).join(' ');
          const navItemClassNameModifiers = classNames(
            iconOnlyScreenSizeModifiers,
            modifiers,
            screenSizeModifiers,
          );

          if (!screenSizeModifiers) return null;

          let navItemElement: ReactNode = null;
          if ('component' in navItemConfig && navItemConfig.component) {
            // Only support React elements, not component types
            if (isValidElement(navItemConfig.component)) {
              navItemElement = cloneElement(
                navItemConfig.component,
                {
                  ...navItemConfig.component.props,
                  classNameModifier: classNames(
                    navItemConfig.component.props?.classNameModifier,
                    navItemClassNameModifiers,
                  ),
                },
              );
            }
          } else {
            navItemElement = (
              <NavItem
                classNameModifier={navItemClassNameModifiers}
                to={'path' in navItemConfig ? navItemConfig.path : ''}
                icon={navItemConfig.icon}
              >
                {t(navItemConfig.i18nKey ?? '')}
              </NavItem>
            );
          }

          return (
            <TopNavItemWrapper
              key={navItemConfig.key || navItemConfig.i18nKey}
              classNameModifier={navItemClassNameModifiers}
            >
              {navItemElement}
            </TopNavItemWrapper>
          );
        })}

        {!isLoggedIn && <DestinationNavigation />}
        {!isLoggedIn && <UnauthenticatedNavItems classNameModifier={modifiers} />}

        <li className={getClassNames(style, 'item', `${modifiers} dropdown`)}>
          <ScreenSizeWrapper screenSizeWrapper="mDown">
            <NavModal
              classNameModifier="menuItems"
              heading={(
                <div className={style.hamburger}>
                  <Icon id="menu" size="l" />
                </div>
              )}
            >
              {({ close: closeModal }) => (
                <SubNavigation
                  close={closeModal}
                  nestedMenuVariant="sidebar"
                />
              )}
            </NavModal>
          </ScreenSizeWrapper>
          <ScreenSizeWrapper screenSizeWrapper="m">
            <NavDropdown
              className={getClassNames(style, 'link', `${modifiers}`)}
              classNameModifier="nestedMenu"
              withChevron={false}
              heading={(
                <div className={style.hamburger}>
                  <Icon id="menu" size="l" />
                </div>
              )}
              aria-label="Main menu"
              data-test="MainMenuDropDown"
            >
              {({ close: closeModal }) => (
                <SubNavigation
                  close={closeModal}
                />
              )}
            </NavDropdown>
          </ScreenSizeWrapper>
        </li>
      </ul>
    </nav>
  );
};

export default Navigation;
